import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  FileText, 
  Building2, 
  CreditCard, 
  Hash, 
  Loader2, 
  CheckCircle,
  AlertCircle,
  Upload,
  Eye
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface InvoiceSettings {
  id?: string;
  invoice_enabled: boolean;
  business_name: string;
  business_address: string;
  business_city: string;
  business_state: string;
  business_pincode: string;
  business_phone: string;
  business_email: string;
  logo_url: string;
  gst_number: string;
  pan_number: string;
  bank_name: string;
  bank_account_number: string;
  bank_ifsc: string;
  bank_account_name: string;
  invoice_prefix: string;
  next_invoice_number: number;
  due_date_days: number;
  payment_terms: string;
  footer_notes: string;
  delivery_method: 'pdf' | 'inline' | 'both';
}

interface InvoiceSettingsFormProps {
  merchantId: string;
  isOnboarding?: boolean;
  onComplete?: () => void;
  initialData?: Partial<InvoiceSettings>;
}

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Other"
];

export function InvoiceSettingsForm({ 
  merchantId, 
  isOnboarding = false, 
  onComplete,
  initialData 
}: InvoiceSettingsFormProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [settings, setSettings] = useState<InvoiceSettings>({
    invoice_enabled: false,
    business_name: '',
    business_address: '',
    business_city: '',
    business_state: '',
    business_pincode: '',
    business_phone: '',
    business_email: '',
    logo_url: '',
    gst_number: '',
    pan_number: '',
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_account_name: '',
    invoice_prefix: 'INV',
    next_invoice_number: 1,
    due_date_days: 7,
    payment_terms: '',
    footer_notes: '',
    delivery_method: 'pdf',
    ...initialData
  });

  useEffect(() => {
    fetchSettings();
  }, [merchantId]);

  const fetchSettings = async () => {
    try {
      // First fetch from invoice settings table
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('professional_invoice_settings')
        .select('*')
        .eq('merchant_id', merchantId)
        .maybeSingle();

      if (!invoiceError && invoiceData) {
        setSettings({
          id: invoiceData.id,
          invoice_enabled: invoiceData.invoice_enabled,
          business_name: invoiceData.business_name || '',
          business_address: invoiceData.business_address || '',
          business_city: invoiceData.business_city || '',
          business_state: invoiceData.business_state || '',
          business_pincode: invoiceData.business_pincode || '',
          business_phone: invoiceData.business_phone || '',
          business_email: invoiceData.business_email || '',
          logo_url: invoiceData.logo_url || '',
          gst_number: invoiceData.gst_number || '',
          pan_number: invoiceData.pan_number || '',
          bank_name: invoiceData.bank_name || '',
          bank_account_number: invoiceData.bank_account_number || '',
          bank_ifsc: invoiceData.bank_ifsc || '',
          bank_account_name: invoiceData.bank_account_name || '',
          invoice_prefix: invoiceData.invoice_prefix || 'INV',
          next_invoice_number: invoiceData.next_invoice_number || 1,
          due_date_days: invoiceData.due_date_days || 7,
          payment_terms: invoiceData.payment_terms || '',
          footer_notes: invoiceData.footer_notes || '',
          delivery_method: (invoiceData.delivery_method as 'pdf' | 'inline' | 'both') || 'pdf',
        });
      } else {
        // Try to pre-fill from merchant data
        const { data: merchantData } = await supabase
          .from('merchants')
          .select('store_name, business_address, business_phone, business_email, gst_number, pan_number, logo_url')
          .eq('id', merchantId)
          .single();

        if (merchantData) {
          setSettings(prev => ({
            ...prev,
            business_name: merchantData.store_name || '',
            business_address: merchantData.business_address || '',
            business_phone: merchantData.business_phone || '',
            business_email: merchantData.business_email || '',
            gst_number: merchantData.gst_number || '',
            pan_number: merchantData.pan_number || '',
            logo_url: merchantData.logo_url || '',
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching invoice settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${merchantId}-invoice-logo.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName);

      setSettings(prev => ({ ...prev, logo_url: publicUrl }));
      toast.success('Logo uploaded');
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        merchant_id: merchantId,
        invoice_enabled: settings.invoice_enabled,
        business_name: settings.business_name || null,
        business_address: settings.business_address || null,
        business_city: settings.business_city || null,
        business_state: settings.business_state || null,
        business_pincode: settings.business_pincode || null,
        business_phone: settings.business_phone || null,
        business_email: settings.business_email || null,
        logo_url: settings.logo_url || null,
        gst_number: settings.gst_number || null,
        pan_number: settings.pan_number || null,
        bank_name: settings.bank_name || null,
        bank_account_number: settings.bank_account_number || null,
        bank_ifsc: settings.bank_ifsc || null,
        bank_account_name: settings.bank_account_name || null,
        invoice_prefix: settings.invoice_prefix || 'INV',
        next_invoice_number: settings.next_invoice_number || 1,
        due_date_days: settings.due_date_days || 7,
        payment_terms: settings.payment_terms || null,
        footer_notes: settings.footer_notes || null,
        delivery_method: settings.delivery_method,
      };

      if (settings.id) {
        const { error } = await supabase
          .from('professional_invoice_settings')
          .update(payload)
          .eq('id', settings.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('professional_invoice_settings')
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        setSettings(prev => ({ ...prev, id: data.id }));
      }

      toast.success('Invoice settings saved!');
      onComplete?.();
    } catch (error: any) {
      console.error('Error saving invoice settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const generatePreviewNumber = () => {
    const prefix = settings.invoice_prefix || 'INV';
    const number = settings.next_invoice_number || 1;
    const month = new Date().toISOString().slice(0, 7).replace('-', '');
    return `${prefix}-${month}-${String(number).padStart(4, '0')}`;
  };

  // Validation checks
  const isPanValid = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(settings.pan_number.toUpperCase());
  const isGstValid = !settings.gst_number || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(settings.gst_number.toUpperCase());
  const isIfscValid = !settings.bank_ifsc || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(settings.bank_ifsc.toUpperCase());
  
  const isFormValid = !settings.invoice_enabled || (
    settings.business_name &&
    settings.business_email
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Master Toggle */}
      <Card className={`border-l-4 ${settings.invoice_enabled ? 'border-l-cyan-500' : 'border-l-muted'}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${settings.invoice_enabled ? 'bg-cyan-500/10' : 'bg-muted'}`}>
                <FileText className={`h-5 w-5 ${settings.invoice_enabled ? 'text-cyan-600' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <CardTitle className="text-lg">Generate Invoices</CardTitle>
                <CardDescription>
                  Automatically create and send invoices with payment requests
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.invoice_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, invoice_enabled: checked }))}
              className="data-[state=checked]:bg-cyan-500"
            />
          </div>
        </CardHeader>
      </Card>

      {settings.invoice_enabled && (
        <>
          {/* Business Details */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-cyan-600" />
                <CardTitle className="text-base">Business Details</CardTitle>
              </div>
              <CardDescription>
                Information displayed in the invoice header
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Business Logo</Label>
                <div className="flex items-center gap-4">
                  {settings.logo_url ? (
                    <img 
                      src={settings.logo_url} 
                      alt="Logo" 
                      className="h-16 w-16 object-contain rounded border"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded border border-dashed flex items-center justify-center bg-muted/30">
                      <FileText className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      disabled={uploadingLogo}
                    >
                      <label htmlFor="logo-upload" className="cursor-pointer">
                        {uploadingLogo ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Upload Logo
                      </label>
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">PNG or JPG, max 2MB</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name *</Label>
                  <Input
                    value={settings.business_name}
                    onChange={(e) => setSettings(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder="Your Business Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Business Email *</Label>
                  <Input
                    type="email"
                    value={settings.business_email}
                    onChange={(e) => setSettings(prev => ({ ...prev, business_email: e.target.value }))}
                    placeholder="billing@yourbusiness.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={settings.business_address}
                  onChange={(e) => setSettings(prev => ({ ...prev, business_address: e.target.value }))}
                  placeholder="Street address, Building name, etc."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={settings.business_city}
                    onChange={(e) => setSettings(prev => ({ ...prev, business_city: e.target.value }))}
                    placeholder="Mumbai"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select
                    value={settings.business_state}
                    onValueChange={(value) => setSettings(prev => ({ ...prev, business_state: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input
                    value={settings.business_pincode}
                    onChange={(e) => setSettings(prev => ({ ...prev, business_pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    placeholder="400001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={settings.business_phone}
                    onChange={(e) => setSettings(prev => ({ ...prev, business_phone: e.target.value }))}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>

              <Separator />

              {/* Tax Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    GST Number
                    {settings.gst_number && (
                      isGstValid ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <AlertCircle className="h-3 w-3 text-destructive" />
                      )
                    )}
                  </Label>
                  <Input
                    value={settings.gst_number}
                    onChange={(e) => setSettings(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }))}
                    placeholder="22AAAAA0000A1Z5"
                    className={settings.gst_number && !isGstValid ? 'border-destructive' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    PAN Number
                    {settings.pan_number && (
                      isPanValid ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <AlertCircle className="h-3 w-3 text-destructive" />
                      )
                    )}
                  </Label>
                  <Input
                    value={settings.pan_number}
                    onChange={(e) => setSettings(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))}
                    placeholder="ABCDE1234F"
                    className={settings.pan_number && !isPanValid ? 'border-destructive' : ''}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Details */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-cyan-600" />
                <CardTitle className="text-base">Bank Details</CardTitle>
              </div>
              <CardDescription>
                Payment information displayed on the invoice
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Holder Name</Label>
                  <Input
                    value={settings.bank_account_name}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_account_name: e.target.value }))}
                    placeholder="Your Name or Business Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={settings.bank_name}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_name: e.target.value }))}
                    placeholder="HDFC Bank"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={settings.bank_account_number}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_account_number: e.target.value.replace(/\D/g, '') }))}
                    placeholder="1234567890123"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    IFSC Code
                    {settings.bank_ifsc && (
                      isIfscValid ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <AlertCircle className="h-3 w-3 text-destructive" />
                      )
                    )}
                  </Label>
                  <Input
                    value={settings.bank_ifsc}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_ifsc: e.target.value.toUpperCase() }))}
                    placeholder="HDFC0001234"
                    className={settings.bank_ifsc && !isIfscValid ? 'border-destructive' : ''}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Numbering & Terms */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Hash className="h-5 w-5 text-cyan-600" />
                <CardTitle className="text-base">Invoice Format & Terms</CardTitle>
              </div>
              <CardDescription>
                Customize invoice numbering and payment terms
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Invoice Prefix</Label>
                  <Input
                    value={settings.invoice_prefix}
                    onChange={(e) => setSettings(prev => ({ ...prev, invoice_prefix: e.target.value.toUpperCase().slice(0, 5) }))}
                    placeholder="INV"
                    maxLength={5}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Next Invoice Number</Label>
                  <Input
                    type="number"
                    min={1}
                    value={settings.next_invoice_number}
                    onChange={(e) => setSettings(prev => ({ ...prev, next_invoice_number: parseInt(e.target.value) || 1 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="h-9 px-3 rounded-md border bg-muted/50 flex items-center">
                    <Badge variant="outline" className="font-mono">
                      {generatePreviewNumber()}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Payment Due (days)</Label>
                  <Select
                    value={String(settings.due_date_days)}
                    onValueChange={(value) => setSettings(prev => ({ ...prev, due_date_days: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Due on receipt</SelectItem>
                      <SelectItem value="7">Net 7 days</SelectItem>
                      <SelectItem value="15">Net 15 days</SelectItem>
                      <SelectItem value="30">Net 30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Delivery Method</Label>
                  <Select
                    value={settings.delivery_method}
                    onValueChange={(value: 'pdf' | 'inline' | 'both') => setSettings(prev => ({ ...prev, delivery_method: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF Attachment</SelectItem>
                      <SelectItem value="inline">Inline in Email</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Payment Terms (optional)</Label>
                <Textarea
                  value={settings.payment_terms}
                  onChange={(e) => setSettings(prev => ({ ...prev, payment_terms: e.target.value }))}
                  placeholder="e.g., Late payments will incur a 2% monthly fee..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Footer Notes (optional)</Label>
                <Textarea
                  value={settings.footer_notes}
                  onChange={(e) => setSettings(prev => ({ ...prev, footer_notes: e.target.value }))}
                  placeholder="Thank you for your business!"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Actions */}
      <div className={`flex ${isOnboarding ? 'justify-end' : 'justify-between'} gap-4`}>
        {!isOnboarding && (
          <Button variant="outline" onClick={() => window.history.back()}>
            Cancel
          </Button>
        )}
        <Button 
          onClick={handleSave} 
          disabled={saving || !isFormValid}
          className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
        >
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isOnboarding ? 'Save & Continue' : 'Save Invoice Settings'}
        </Button>
      </div>
    </div>
  );
}
